import { useCallback, useEffect, useRef, useState } from 'react';
import { trackEvent } from '../firebase/config.js';
import { Events } from '../firebase/events.js';

export function useAudioRecording({
    duration,
    isSafari,
    setNotification,
    generateFlashcardRequest,
}) {
    const [isListening, setIsListening] = useState(false);
    const [voiceActivated, setVoiceActivated] = useState(false);
    const [listeningDuration, setListeningDuration] = useState(1);
    const [isAutoFlashOn, setIsAutoFlashOn] = useState(false);
    const [autoFlashInterval, setAutoFlashInterval] = useState(20);
    const [transcriptText, setTranscriptText] = useState('');
    const [interimTranscript, setInterimTranscript] = useState('');

    const streamRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const headerChunkRef = useRef(null);
    const recognitionRef = useRef(null);
    const autoFlashTimerRef = useRef(null);
    const isRequestInFlightRef = useRef(false);
    const lastVoiceTriggerAtRef = useRef(0);
    const listeningTimeoutRef = useRef(null);
    const [captureLastSeconds, setCaptureLastSeconds] = useState(15);
    const isSpeechApiAvailable = typeof window !== 'undefined' && (
        window.SpeechRecognition || window.webkitSpeechRecognition
    );

    const isAutoFlashOnRef = useRef(isAutoFlashOn);
    useEffect(() => { isAutoFlashOnRef.current = isAutoFlashOn; }, [isAutoFlashOn]);
    const voiceActivatedRef = useRef(voiceActivated);
    useEffect(() => { voiceActivatedRef.current = voiceActivated; }, [voiceActivated]);

    // Hydrate initial duration from provider prop (supports 0 for continuous)
    useEffect(() => {
        if (typeof duration === 'number' && !Number.isNaN(duration)) {
            setListeningDuration(duration);
        }
    }, [duration]);

    const buildAudioBlob = useCallback(() => {
        let parts = audioChunksRef.current;
        // If there are no media chunks yet, return empty blob
        if (!parts || parts.length === 0) {
            return new Blob([], { type: 'audio/webm' });
        }
        if (captureLastSeconds && captureLastSeconds > 0) {
            const approxChunkSeconds = 1; // we start recorder with 1000ms timeslice
            const keep = Math.max(1, Math.ceil(captureLastSeconds / approxChunkSeconds));
            parts = parts.slice(Math.max(0, parts.length - keep));
        }
        // Get actual mime type from recorder or use default
        const mimeType = (mediaRecorderRef.current && mediaRecorderRef.current.mimeType) || 
                        (isSafari ? 'audio/mp4' : 'audio/webm');
        return new Blob(parts, { type: mimeType });
    }, [isSafari, captureLastSeconds]);

    const blobToBase64 = (blob) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });

    const lastLiveFlashAtRef = useRef(0);

    const handleLiveFlashIt = useCallback(async () => {
        try {
            // Short debounce to avoid rapid double submissions (reduce to feel more responsive)
            const now = Date.now();
            if (now - (lastLiveFlashAtRef.current || 0) < 250) return;
            lastLiveFlashAtRef.current = now;

            if (isRequestInFlightRef.current) {
                // Provide subtle feedback if user clicks while busy
                setNotification('Working...');
                return;
            }
            // For auto-flash, we need to stop and restart the recorder to get a complete file
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                isRequestInFlightRef.current = true;
                
                // Stop recording and wait for the final chunk
                const completeBlob = await new Promise((resolve) => {
                    const chunks = [...audioChunksRef.current];
                    
                    const handleStop = () => {
                        // Build blob from all chunks including the final one
                        const mimeType = mediaRecorderRef.current.mimeType || 'audio/webm';
                        const blob = new Blob(chunks, { type: mimeType });
                        console.log(`[audio] Complete recording: ${chunks.length} chunks, ${blob.size} bytes`);
                        resolve(blob);
                    };
                    
                    const handleData = (event) => {
                        if (event.data && event.data.size > 0) {
                            chunks.push(event.data);
                            console.log(`[audio] Final chunk: ${event.data.size} bytes`);
                        }
                    };
                    
                    try {
                        mediaRecorderRef.current.addEventListener('dataavailable', handleData, { once: true });
                        mediaRecorderRef.current.addEventListener('stop', handleStop, { once: true });
                        mediaRecorderRef.current.stop();
                    } catch (e) {
                        resolve(null);
                    }
                });
                
                if (!completeBlob || completeBlob.size < 1000) {
                    setNotification('Recording too short. Please speak for longer.');
                    return;
                }
                
                const base64Audio = await blobToBase64(completeBlob);
                const approxDurationSec = captureLastSeconds && captureLastSeconds > 0
                    ? Number(captureLastSeconds)
                    : (listeningDuration > 0 ? Number(listeningDuration) * 60 : 15);
                const mt = completeBlob.type || 'audio/webm';
                
                console.log(`[audio] Sending complete blob: size=${completeBlob.size}, type=${mt}`);
                await generateFlashcardRequest({ audio_data: base64Audio, is_live_capture: true, duration: approxDurationSec, mime_type: mt });
                
                // Clear transcript and restart recording
                setTranscriptText('');
                setInterimTranscript('');
                audioChunksRef.current = [];
                
                // Restart recording if still listening
                if (isListening && streamRef.current) {
                    mediaRecorderRef.current.start();
                    console.log('[audio] Recording restarted after flash');
                }
                
                lastVoiceTriggerAtRef.current = Date.now();
            } else {
                setNotification('No active recording. Please start listening first.');
            }
        } catch (err) {
            console.error('handleLiveFlashIt error', err);
            setNotification(`Error: ${err.message || 'Could not generate from live audio.'}`);
        } finally {
            isRequestInFlightRef.current = false;
        }
    }, [generateFlashcardRequest, setNotification, isListening]);

    useEffect(() => {
        if (autoFlashTimerRef.current) clearInterval(autoFlashTimerRef.current);
        autoFlashTimerRef.current = null;
        if (isListening && isAutoFlashOn) {
            // Wait a few seconds before first auto-flash to ensure audio chunks are captured
            const firstFlashDelay = Math.max(3000, autoFlashInterval * 1000);
            const firstTimer = setTimeout(() => {
                if (isAutoFlashOnRef.current) handleLiveFlashIt();
                // Then set regular interval
                autoFlashTimerRef.current = setInterval(() => {
                    if (isAutoFlashOnRef.current) handleLiveFlashIt();
                }, autoFlashInterval * 1000);
            }, firstFlashDelay);
            return () => {
                clearTimeout(firstTimer);
                clearInterval(autoFlashTimerRef.current);
            };
        }
        return () => clearInterval(autoFlashTimerRef.current);
    }, [isListening, isAutoFlashOn, autoFlashInterval, handleLiveFlashIt]);

    const startListening = useCallback(async () => {
        try {
            // Request mic with built-in echo/noise suppression to avoid picking up device TTS
            streamRef.current = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true
                }
            });
            setIsListening(true);
            setNotification('Listening...');
            try { trackEvent(Events.listening_start); } catch {}

            const mimeType = isSafari ? 'audio/mp4' : 'audio/webm; codecs=opus';
            
            // Check if browser actually supports the requested mime type
            let actualMimeType = mimeType;
            if (!isSafari && MediaRecorder.isTypeSupported) {
                if (!MediaRecorder.isTypeSupported(mimeType)) {
                    console.warn(`Browser doesn't support ${mimeType}, trying alternatives...`);
                    const alternatives = ['audio/webm', 'audio/ogg', 'audio/mp4'];
                    actualMimeType = alternatives.find(mt => MediaRecorder.isTypeSupported(mt)) || '';
                    console.log(`Using mime type: ${actualMimeType || 'default'}`);
                }
            }
            
            mediaRecorderRef.current = new MediaRecorder(streamRef.current, actualMimeType ? { mimeType: actualMimeType } : {});
            audioChunksRef.current = [];
            headerChunkRef.current = null;

            mediaRecorderRef.current.ondataavailable = (event) => {
                if (event.data && event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                    console.log(`[audio] Chunk received: size=${event.data.size}, total chunks=${audioChunksRef.current.length}`);
                }
            };
            
            mediaRecorderRef.current.onerror = (event) => {
                console.error('[audio] MediaRecorder error:', event);
                setNotification('Recording error occurred. Please try again.');
            };
            
            mediaRecorderRef.current.onstop = () => {
                console.log(`[audio] MediaRecorder stopped. Total chunks: ${audioChunksRef.current.length}`);
            };

            // Start recording without timeslice to get a complete file
            try {
                mediaRecorderRef.current.start();
                // For auto-flash, we'll manually stop/restart to get complete chunks
                console.log(`[audio] MediaRecorder started with mimeType: ${mediaRecorderRef.current.mimeType}`);
            } catch (err) {
                console.error('[audio] Failed to start MediaRecorder:', err);
                throw err;
            }

            // Start Web Speech API for live transcription (and optional voice activation)
            if (!isSafari && isSpeechApiAvailable) {
                const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
                if (SpeechRecognition) {
                    recognitionRef.current = new SpeechRecognition();
                    recognitionRef.current.continuous = true;
                    recognitionRef.current.interimResults = true;
                    try { recognitionRef.current.lang = 'en-US'; } catch {}
                    try { recognitionRef.current.maxAlternatives = 1; } catch {}
                    recognitionRef.current.onresult = (event) => {
                        // Accumulate only new results from resultIndex to avoid duplicates
                        let interim = '';
                        for (let i = event.resultIndex; i < event.results.length; i++) {
                            const res = event.results[i];
                            const text = (res[0] && res[0].transcript) ? res[0].transcript : '';
                            if (res.isFinal) {
                                setTranscriptText(prev => prev + (prev ? ' ' : '') + text);
                                const normalized = text.toLowerCase().trim();
                                const shouldTrigger = /\bflash( it)?\b/.test(normalized) || normalized === 'flash it' || normalized === 'flash';
                                if (voiceActivatedRef.current && shouldTrigger) {
                                    console.log('[voice] Trigger phrase detected:', normalized);
                                    const now = Date.now();
                                    if (!isRequestInFlightRef.current && now - (lastVoiceTriggerAtRef.current || 0) > 2500) {
                                        handleLiveFlashIt();
                                    }
                                }
                            } else {
                                interim += text;
                            }
                        }
                        setInterimTranscript(interim);
                    };
                    recognitionRef.current.onerror = () => {
                        // No-op; rely on onend to attempt restart
                    };
                    recognitionRef.current.onend = () => {
                        // Attempt to keep recognition running while user is listening
                        if (isListening) {
                            try { recognitionRef.current.start(); } catch {}
                        }
                    };
                    try { recognitionRef.current.start(); } catch {}
                }
            }

            if (listeningDuration > 0) {
                listeningTimeoutRef.current = setTimeout(() => {
                    if (isAutoFlashOnRef.current) {
                        setNotification('Listening timer finished. Generating final card...');
                        handleLiveFlashIt();
                        setTimeout(() => stopListening(), 2500);
                    } else {
                        setNotification(`Listening timer finished.`);
                        stopListening();
                    }
                }, listeningDuration * 60 * 1000);
            } else {
                // 0 minutes means continuous listening until user stops
                setNotification('Listening... (no timer)');
            }
        } catch (err) {
            console.warn('Microphone access denied or error', err);
            setNotification('Microphone access denied or error. Please check permissions and try again.');
            setIsListening(false);
        }
    }, [isSafari, listeningDuration, voiceActivated, handleLiveFlashIt, setNotification]);

    const stopListening = useCallback(() => {
        if (listeningTimeoutRef.current) clearTimeout(listeningTimeoutRef.current);
        if (autoFlashTimerRef.current) clearInterval(autoFlashTimerRef.current);
        if (recognitionRef.current) {
            try { recognitionRef.current.stop(); } catch {}
            recognitionRef.current = null;
        }
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            try { mediaRecorderRef.current.stop(); } catch {}
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop());
            streamRef.current = null;
        }
        setIsListening(false);
        setInterimTranscript('');
        try { trackEvent(Events.listening_stop); } catch {}
    }, []);

    // Expose a way to capture a complete clip for other features (e.g., games)
    const captureClip = useCallback(async () => {
        if (!mediaRecorderRef.current || mediaRecorderRef.current.state !== 'recording') {
            return null;
        }
        const completeBlob = await new Promise((resolve) => {
            const chunks = [...audioChunksRef.current];

            const handleStop = () => {
                const mimeType = mediaRecorderRef.current && mediaRecorderRef.current.mimeType ? mediaRecorderRef.current.mimeType : (isSafari ? 'audio/mp4' : 'audio/webm');
                const blob = new Blob(chunks, { type: mimeType });
                resolve(blob);
            };

            const handleData = (event) => {
                if (event.data && event.data.size > 0) {
                    chunks.push(event.data);
                }
            };

            mediaRecorderRef.current.addEventListener('dataavailable', handleData, { once: true });
            mediaRecorderRef.current.addEventListener('stop', handleStop, { once: true });
            try { mediaRecorderRef.current.stop(); } catch { resolve(null); }
        });

        if (!completeBlob || completeBlob.size < 100) {
            return null;
        }

        const approxDurationSec = captureLastSeconds && captureLastSeconds > 0
            ? Number(captureLastSeconds)
            : (listeningDuration > 0 ? Number(listeningDuration) * 60 : 15);
        const mt = completeBlob.type || (isSafari ? 'audio/mp4' : 'audio/webm');

        // Restart recording if still listening
        if (isListening && streamRef.current) {
            try { audioChunksRef.current = []; mediaRecorderRef.current.start(); } catch {}
        }

        return { blob: completeBlob, mimeType: mt, durationSec: approxDurationSec };
    }, [captureLastSeconds, listeningDuration, isListening, isSafari]);

    useEffect(() => () => {
        try { stopListening(); } catch {}
    }, [stopListening]);

    // Keep UI and mic state in sync when the page loses visibility (user switches browser tabs)
    useEffect(() => {
        const onVisibilityChange = () => {
            try {
                if (document.visibilityState === 'hidden' && isListening) {
                    stopListening();
                }
            } catch {}
        };
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', onVisibilityChange);
        }
        return () => {
            try { document.removeEventListener('visibilitychange', onVisibilityChange); } catch {}
        };
    }, [isListening, stopListening]);

    return {
        isListening,
        voiceActivated,
        listeningDuration,
        isAutoFlashOn,
        autoFlashInterval,
        captureLastSeconds,
        transcriptText,
        interimTranscript,
        isSpeechApiAvailable: !!isSpeechApiAvailable,
        setVoiceActivated,
        setListeningDuration,
        setIsAutoFlashOn,
        setAutoFlashInterval,
        setCaptureLastSeconds,
        clearTranscript: () => { setTranscriptText(''); setInterimTranscript(''); },
        startListening,
        stopListening,
        handleLiveFlashIt,
        captureClip
    };
}


